import React, { useCallback, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Button } from '@artstorefronts/ui';
import { useMutation } from '@apollo/client';
import { browserLocaleToPersist } from '@arthelper/shared';

import AuthLayout from './layouts/authLayout';
import AppLayout from './layouts/dashboardLayout';
import FullLayout from './layouts/fullLayout';
import { SparkProvider } from './context/spark';

import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import { AttributionRedirectToSignIn, AttributionRedirectToSignUp } from './components/AttributionAuthRedirect';
import OnboardingAccessGuard from './components/OnboardingAccessGuard';
import { OnboardingRedirect } from './components/OnboardingRedirect';
import { FeatureWizardHost } from './components/featureWizard';
import { ArtyFlyoutProvider } from './components/arty/flyout';
import { InitiateCheckoutTracker } from './components/InitiateCheckoutTracker';
import { NotificationEmailAttribution } from './components/NotificationEmailAttribution';
import ProfileRedirect from './pages/profile';
import { UserDataLayer } from './components/UserDataLayer';
import { FeatureGate } from './components/FeatureGate';
import { WELCOME_FLOW_FLAG } from './constants/onboarding';

import { useUpdateChecker } from './hooks/useUpdateChecker';
import { useAuth } from './context/auth';
import { ProfileProvider } from './context/profile';
import { InviteModalProvider } from './context/inviteModal';
import { MediaDropLabels } from './components/MediaDropLabels';
import { ROUTES_CONFIG } from './routes-config';
import { useUtmParams } from './hooks/useUtmParams';
import { useBodyDataAttributes } from './hooks/useBodyDataAttributes';
import { useProfileLocaleSync } from './hooks/useProfileLocaleSync';
import { UPDATE_PROFILE } from './graphql/mutations';

// Lazy load all pages for code splitting
const Dashboard = lazy(() => import('./pages/dashboard'));
const Art = lazy(() => import('./pages/art'));
const Chat = lazy(() => import('./pages/Chat'));
const Assistant = lazy(() => import('./pages/Assistant'));
const SharedChat = lazy(() => import('./pages/SharedChat'));
const EvalChat = lazy(() => import('./pages/EvalChat'));
const NotFound = lazy(() => import('./pages/notFound'));
const Settings = lazy(() => import('./pages/settings'));
const AddArtwork = lazy(() => import('./pages/addArtwork'));
const Plans = lazy(() => import('./pages/plans'));
const Billing = lazy(() => import('./pages/billing'));
const Saved = lazy(() => import('./pages/saved'));
const Tutorials = lazy(() => import('./pages/tutorials'));
const Calendar = lazy(() => import('./pages/calendar'));
const Onboarding = lazy(() => import('./pages/onboarding'));
const Welcome = lazy(() => import('./pages/welcome'));
const GrowAudience = lazy(() => import('./pages/growAudience'));
const ArtMarketingAutopilot = lazy(() => import('./pages/artMarketingAutopilot'));
// const Contacts = lazy(() => import('./pages/Contacts'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Unsubscribe = lazy(() => import('./pages/unsubscribe'));
const CommunityDigestMute = lazy(() => import('./pages/community-digest-mute'));
const Integrations = lazy(() => import('./pages/integrations'));
const Post = lazy(() => import('./pages/post'));
const DebugAuth = lazy(() => import('./pages/DebugAuth'));
const DebugSignin = lazy(() => import('./pages/DebugSignin'));
const Messages = lazy(() => import('./pages/Messages'));
const MessageDetail = lazy(() => import('./pages/MessageDetail'));
const MessagesNew = lazy(() => import('./pages/messages/new'));
const Network = lazy(() => import('./pages/network'));
const NetworkConnections = lazy(() => import('./pages/network/connections'));
const NetworkDiscover = lazy(() => import('./pages/network/discover'));
const NetworkRequests = lazy(() => import('./pages/network/requests'));

const CONNECTIONS_FLAG = 'connections_and_dms';

// Admin pages (lazy loaded, only for admins)
const Admin = lazy(() => import('./pages/admin'));
const AdminUsers = lazy(() => import('./pages/admin/users'));
const AdminUserEdit = lazy(() => import('./pages/admin/users/edit'));
const AdminBadges = lazy(() => import('./pages/admin/badges'));
const AdminContests = lazy(() => import('./pages/admin/contests'));
const AdminFeedCards = lazy(() => import('./pages/admin/feed-cards'));
const AdminFeedStrategies = lazy(() => import('./pages/admin/feed-strategies'));
const AdminQuests = lazy(() => import('./pages/admin/quests'));
const AdminQuestEditor = lazy(() => import('./pages/admin/quests/editor'));
const AdminBounties = lazy(() => import('./pages/admin/bounties'));
const AdminBountyEditor = lazy(() => import('./pages/admin/bounties/editor'));
const AdminReferrals = lazy(() => import('./pages/admin/referrals'));
const AdminDmTranslation = lazy(() => import('./pages/admin/dm-translation'));
const AdminPreFeeds = lazy(() => import('./pages/admin/pre-feeds'));
const AdminPreFeedEditor = lazy(() => import('./pages/admin/pre-feeds/editor'));
const AdminSparklinks = lazy(() => import('./pages/admin/sparklinks'));
const AdminMarketingLinks = lazy(() => import('./pages/admin/marketing-links'));
const AdminVerificationRequests = lazy(() => import('./pages/admin/verification-requests'));
const AdminRewards = lazy(() => import('./pages/admin/rewards'));
const AdminPoints = lazy(() => import('./pages/admin/points'));
const AdminSparks = lazy(() => import('./pages/admin/sparks'));
const AdminSparkEdit = lazy(() => import('./pages/admin/sparks/edit'));
const AdminSparkAdd = lazy(() => import('./pages/admin/sparks/add'));
const AdminHome = lazy(() => import('./pages/admin/home'));
const AdminMetrics = lazy(() => import('./pages/admin/metrics'));
const AdminFeatures = lazy(() => import('./pages/admin/features'));
const AdminNavConfig = lazy(() => import('./pages/admin/nav-config'));
const AdminMocks = lazy(() => import('./pages/admin/mocks'));
const AdminChats = lazy(() => import('./pages/admin/chats'));
const AdminChatDetail = lazy(() => import('./pages/admin/chats/detail'));
const AdminProfiles = lazy(() => import('./pages/admin/profiles'));
const AdminProfileEdit = lazy(() => import('./pages/admin/profiles/edit'));
const AdminImportArtists = lazy(() => import('./pages/admin/import-artists'));
const AdminCommunityInvites = lazy(() => import('./pages/admin/community-invites'));
const AdminArtistSpotlight = lazy(() => import('./pages/admin/artist-spotlight'));
const AdminModeration = lazy(() => import('./pages/admin/moderation'));
const AdminCommunity = lazy(() => import('./pages/admin/community'));
const AdminGeographicCentrality = lazy(() => import('./pages/admin/geographic-centrality'));
const AdminArtistGenome = lazy(() => import('./pages/admin/artist-genome'));
const AdminOnboarding = lazy(() => import('./pages/admin/onboarding'));
const AdminOnboardingDetail = lazy(() => import('./pages/admin/onboarding/[id]'));
const AdminOnboardingEditor = lazy(() => import('./pages/admin/onboarding/editor'));
const AdminOnboardingCompare = lazy(() => import('./pages/admin/onboarding/compare'));
const AdminOnboardingInspector = lazy(() => import('./pages/admin/onboarding/inspector'));
const AdminAcquisition = lazy(() => import('./pages/admin/acquisition'));
const AdminAcquisitionEditor = lazy(() => import('./pages/admin/acquisition/editor'));
const AdminAcquisitionCompare = lazy(() => import('./pages/admin/acquisition/compare'));
const AdminAcquisitionWaiting = lazy(() => import('./pages/admin/acquisition/waiting'));
const AdminFeatureWizards = lazy(() => import('./pages/admin/feature-wizards'));
const AdminFeatureWizardEditor = lazy(() => import('./pages/admin/feature-wizards/editor'));

const INTEGRATIONS_ENABLED = import.meta.env.VITE_INTEGRATIONS_ENABLED === 'true';

/**
 * A redirect that carries the query and hash across with it. `<Navigate to="/path" />` drops both,
 * which silently loses parameters the landing URL is the whole point of — `?wizard={slug}` on `/`
 * being exactly the URL a campaign link uses, and `/` being a redirect to the dashboard.
 */
function KeepQueryNavigate({ to }: { to: string }) {
  const { search, hash } = useLocation();

  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

function AnimatedRoutes() {
  const location = useLocation();
  const { hasPermissions } = useAuth();

  const isAdmin = hasPermissions(['role:admin']);

  return (
    <Routes location={location}>
      <Route path="/debug-auth" element={<DebugAuth />} />
      <Route path="/debug-signin" element={<DebugSignin />} />
      <Route
        path="/signin"
        element={
          <>
            <SignedIn>
              <KeepQueryNavigate to="/" />
            </SignedIn>
            <SignedOut>
              <AttributionRedirectToSignIn />
            </SignedOut>
          </>
        }
      />
      <Route
        path="/signup"
        element={
          <>
            <SignedIn>
              <KeepQueryNavigate to="/" />
            </SignedIn>
            <SignedOut>
              <AttributionRedirectToSignUp />
            </SignedOut>
          </>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <>
            <SignedIn>
              <KeepQueryNavigate to="/" />
            </SignedIn>
            <SignedOut>
              <AttributionRedirectToSignIn />
            </SignedOut>
          </>
        }
      />
      <Route
        element={
          <PublicRoute>
            <SparkProvider>
              <AuthLayout />
            </SparkProvider>
          </PublicRoute>
        }
      ></Route>
      <Route element={<FullLayout />}>
        <Route path="/assistant" element={<Assistant />} />
        <Route path={ROUTES_CONFIG['/shared/:chatId'].path} element={<SharedChat />} />
        <Route path={ROUTES_CONFIG['/eval/:chatId'].path} element={<EvalChat />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/community/digest-mute" element={<CommunityDigestMute />} />
      </Route>
      <Route
        element={
          <ProtectedRoute>
            <ArtyFlyoutProvider>
              <AppLayout />
            </ArtyFlyoutProvider>
          </ProtectedRoute>
        }
      >
        {__CHAT_ENABLED__ && (
          <>
            <Route path="/art/chat/:conversationId" element={<Art />} />
            <Route path="/art/chat" element={<Art />} />
            <Route path={ROUTES_CONFIG['/art/:id/chat/:conversationId'].path} element={<Art />} />
            <Route path={ROUTES_CONFIG['/art/:id/chat'].path} element={<Art />} />
            <Route path={ROUTES_CONFIG['/art/chat/:id/:conversationId'].path} element={<Art />} />
            <Route path={ROUTES_CONFIG['/art/chat/:id'].path} element={<Art />} />
            <Route path={ROUTES_CONFIG['/chat/:conversationId'].path} element={<Chat />} />
            <Route path={ROUTES_CONFIG['/chat'].path} element={<Chat />} />
          </>
        )}
        <Route path={ROUTES_CONFIG['/'].path} element={<KeepQueryNavigate to="/my-artwork" />} />
        <Route path={ROUTES_CONFIG['/my-artwork'].path} element={<Dashboard />} />
        <Route path={ROUTES_CONFIG['/art/:id'].path} element={<Art />} />
        <Route path={ROUTES_CONFIG['/art'].path} element={<Art />} />
        <Route path={ROUTES_CONFIG['/saved'].path} element={<Saved />} />
        <Route path={ROUTES_CONFIG['/tutorials'].path} element={<Tutorials />} />
        <Route path={ROUTES_CONFIG['/upload'].path} element={<AddArtwork />} />
        <Route path={ROUTES_CONFIG['/plans'].path} element={<Plans />} />
        <Route path={ROUTES_CONFIG['/billing'].path} element={<Billing />} />
        {/* <Route path={ROUTES_CONFIG['/contacts'].path} element={<Contacts />} /> */}
        <Route path="/notifications" element={<Notifications />} />
        <Route
          path={ROUTES_CONFIG['/onboarding'].path}
          element={
            <OnboardingAccessGuard>
              <Onboarding />
            </OnboardingAccessGuard>
          }
        />
        <Route
          path="/welcome"
          element={
            <FeatureGate flag={WELCOME_FLOW_FLAG}>
              <Welcome />
            </FeatureGate>
          }
        />
        <Route path={ROUTES_CONFIG['/grow-audience'].path} element={<GrowAudience />} />
        <Route path={ROUTES_CONFIG['/grow-your-audience'].path} element={<GrowAudience />} />
        <Route path={ROUTES_CONFIG['/art-marketing-autopilot'].path} element={<ArtMarketingAutopilot />} />
        {/* <Route path={ROUTES_CONFIG['/contacts'].path} element={<Contacts />} /> */}

        {INTEGRATIONS_ENABLED && (
          <>
            <Route path={ROUTES_CONFIG['/integrations'].path} element={<Integrations />} />
            <Route path={ROUTES_CONFIG['/integrations/:name/callback'].path} element={<Integrations />} />
            <Route path={ROUTES_CONFIG['/post'].path} element={<Post />} />
            <Route path={ROUTES_CONFIG['/post/:id'].path} element={<Post />} />
            <Route path={ROUTES_CONFIG['/calendar'].path} element={<Calendar />} />
          </>
        )}

        {isAdmin && (
          <Route path={ROUTES_CONFIG['/admin'].path} element={<Admin />}>
            <Route index element={<Navigate to="/admin/home" replace />} />
            <Route path={ROUTES_CONFIG['/admin/home'].path} element={<AdminHome />} />
            <Route path={ROUTES_CONFIG['/admin/metrics'].path} element={<AdminMetrics />} />
            <Route path={ROUTES_CONFIG['/admin/dashboard'].path} element={<Navigate to="/admin/metrics" replace />} />
            <Route path={ROUTES_CONFIG['/admin/features'].path} element={<AdminFeatures />} />
            <Route path={ROUTES_CONFIG['/admin/nav-config'].path} element={<AdminNavConfig />} />
            <Route path={ROUTES_CONFIG['/admin/users'].path} element={<AdminUsers />} />
            <Route path="/admin/users/:id/edit" element={<AdminUserEdit />} />
            <Route path={ROUTES_CONFIG['/admin/badges'].path} element={<AdminBadges />} />
            <Route path={ROUTES_CONFIG['/admin/contests'].path} element={<AdminContests />} />
            <Route path={ROUTES_CONFIG['/admin/feed-cards'].path} element={<AdminFeedCards />} />
            <Route path={ROUTES_CONFIG['/admin/feed-strategies'].path} element={<AdminFeedStrategies />} />
            <Route path={ROUTES_CONFIG['/admin/quests'].path} element={<AdminQuests />} />
            <Route path={ROUTES_CONFIG['/admin/quests/:id'].path} element={<AdminQuestEditor />} />
            <Route path={ROUTES_CONFIG['/admin/bounties'].path} element={<AdminBounties />} />
            <Route path={ROUTES_CONFIG['/admin/bounties/:id'].path} element={<AdminBountyEditor />} />
            <Route path={ROUTES_CONFIG['/admin/referrals'].path} element={<AdminReferrals />} />
            <Route path={ROUTES_CONFIG['/admin/dm-translation'].path} element={<AdminDmTranslation />} />
            <Route path={ROUTES_CONFIG['/admin/pre-feeds'].path} element={<AdminPreFeeds />} />
            <Route path={ROUTES_CONFIG['/admin/pre-feeds/:id'].path} element={<AdminPreFeedEditor />} />
            <Route path={ROUTES_CONFIG['/admin/sparklinks'].path} element={<AdminSparklinks />} />
            <Route path={ROUTES_CONFIG['/admin/marketing-links'].path} element={<AdminMarketingLinks />} />
            <Route path={ROUTES_CONFIG['/admin/verification-requests'].path} element={<AdminVerificationRequests />} />
            <Route path={ROUTES_CONFIG['/admin/rewards'].path} element={<AdminRewards />} />
            <Route path={ROUTES_CONFIG['/admin/points'].path} element={<AdminPoints />} />
            <Route path={ROUTES_CONFIG['/admin/sparks'].path} element={<AdminSparks />} />
            <Route path={ROUTES_CONFIG['/admin/sparks/edit/:id'].path} element={<AdminSparkEdit />} />
            <Route path={ROUTES_CONFIG['/admin/sparks/add'].path} element={<AdminSparkAdd />} />
            <Route path={ROUTES_CONFIG['/admin/mocks'].path} element={<AdminMocks />} />
            <Route path={ROUTES_CONFIG['/admin/chats'].path} element={<AdminChats />} />
            <Route path={ROUTES_CONFIG['/admin/chats/:id'].path} element={<AdminChatDetail />} />
            <Route path={ROUTES_CONFIG['/admin/community'].path} element={<AdminCommunity />} />
            <Route path={ROUTES_CONFIG['/admin/profiles'].path} element={<AdminProfiles />} />
            <Route path={ROUTES_CONFIG['/admin/profiles/:id'].path} element={<AdminProfileEdit />} />
            <Route path={ROUTES_CONFIG['/admin/import-artists'].path} element={<AdminImportArtists />} />
            <Route path={ROUTES_CONFIG['/admin/community-invites'].path} element={<AdminCommunityInvites />} />
            <Route path={ROUTES_CONFIG['/admin/artist-spotlight'].path} element={<AdminArtistSpotlight />} />
            <Route path={ROUTES_CONFIG['/admin/moderation'].path} element={<AdminModeration />} />
            <Route path={ROUTES_CONFIG['/admin/geographic-centrality'].path} element={<AdminGeographicCentrality />} />
            <Route path={ROUTES_CONFIG['/admin/artist-genome'].path} element={<AdminArtistGenome />} />
            <Route path="/admin/onboarding" element={<AdminOnboarding />} />
            <Route path="/admin/onboarding/:id" element={<AdminOnboardingDetail />} />
            <Route path="/admin/onboarding/editor" element={<AdminOnboardingEditor />} />
            <Route path="/admin/onboarding/editor/:id" element={<AdminOnboardingEditor />} />
            <Route path="/admin/onboarding/compare" element={<AdminOnboardingCompare />} />
            <Route path="/admin/onboarding/inspector" element={<AdminOnboardingInspector />} />
            <Route path={ROUTES_CONFIG['/admin/acquisition'].path} element={<AdminAcquisition />} />
            <Route path={ROUTES_CONFIG['/admin/acquisition/new'].path} element={<AdminAcquisitionEditor />} />
            <Route path={ROUTES_CONFIG['/admin/acquisition/compare'].path} element={<AdminAcquisitionCompare />} />
            <Route path={ROUTES_CONFIG['/admin/acquisition/:id'].path} element={<AdminAcquisitionEditor />} />
            <Route path={ROUTES_CONFIG['/admin/acquisition/:id/waiting'].path} element={<AdminAcquisitionWaiting />} />
            <Route path={ROUTES_CONFIG['/admin/feature-wizards'].path} element={<AdminFeatureWizards />} />
            <Route path={ROUTES_CONFIG['/admin/feature-wizards/new'].path} element={<AdminFeatureWizardEditor />} />
            <Route path={ROUTES_CONFIG['/admin/feature-wizards/:id'].path} element={<AdminFeatureWizardEditor />} />
          </Route>
        )}

        <Route
          path={ROUTES_CONFIG['/settings'].path}
          element={
            <ProfileProvider>
              <Settings />
            </ProfileProvider>
          }
        />
        <Route
          path={ROUTES_CONFIG['/network'].path}
          element={
            <FeatureGate flag={CONNECTIONS_FLAG}>
              <Network />
            </FeatureGate>
          }
        />
        <Route
          path={ROUTES_CONFIG['/network/connections'].path}
          element={
            <FeatureGate flag={CONNECTIONS_FLAG}>
              <NetworkConnections />
            </FeatureGate>
          }
        />
        <Route
          path={ROUTES_CONFIG['/network/requests'].path}
          element={
            <FeatureGate flag={CONNECTIONS_FLAG}>
              <NetworkRequests />
            </FeatureGate>
          }
        />
        <Route
          path={ROUTES_CONFIG['/network/discover'].path}
          element={
            <FeatureGate flag={CONNECTIONS_FLAG}>
              <NetworkDiscover />
            </FeatureGate>
          }
        />
        <Route path={ROUTES_CONFIG['/messages'].path} element={<Messages />} />
        <Route
          path={ROUTES_CONFIG['/messages/new'].path}
          element={
            <FeatureGate flag={CONNECTIONS_FLAG}>
              <MessagesNew />
            </FeatureGate>
          }
        />
        <Route path={ROUTES_CONFIG['/messages/:id'].path} element={<MessageDetail />} />
        {/* Retired editor: bounces to the Town Square profile, where editing now lives. */}
        <Route path={ROUTES_CONFIG['/profile'].path} element={<ProfileRedirect />} />
      </Route>
      <Route path={ROUTES_CONFIG['*'].path} element={<NotFound />} />
    </Routes>
  );
}

function UpdateToastAction() {
  const { t } = useTranslation('common');
  return (
    <div className="flex justify-end items-center flex-grow">
      <Button
        size="sm"
        className="text-white bg-black font-medium text-[13px]"
        onClick={() => window.location.reload()}
      >
        {t('reload')}
      </Button>
    </div>
  );
}

const App: React.FC = () => {
  const { t } = useTranslation('common');
  const { profile } = useAuth();
  const updateAvailable = useUpdateChecker();
  useUtmParams();
  useBodyDataAttributes();

  const profileRef = useRef(profile);
  profileRef.current = profile;

  const [updateProfileMutation] = useMutation(UPDATE_PROFILE);
  const handleSaveLocale = useCallback(
    (locale: string) => {
      const current = profileRef.current;
      const nested = current?.profile;
      // Still loading, or the loaded profile already has a locale (an explicit
      // choice in particular): do not write the browser locale over it.
      const persist = browserLocaleToPersist(
        current && nested ? { locale: nested.locale ?? null, localeSource: nested.localeSource ?? null } : null,
        locale
      );
      if (!persist) return;
      updateProfileMutation({ variables: { input: { locale: persist, localeSource: 'detected' } } }).catch(() => {});
    },
    [updateProfileMutation]
  );

  const localeProfile = profile?.profile
    ? { locale: profile.profile.locale ?? null, localeSource: profile.profile.localeSource ?? null }
    : null;
  useProfileLocaleSync(localeProfile, handleSaveLocale);

  useEffect(() => {
    // The update checker compares against the build stamp baked into this bundle, so it
    // keeps no baseline in storage. This sweeps an 84 KB value returning users still carry
    // under the old key; it can go once the fleet has cycled through a release.
    localStorage.removeItem('manifestHash');
  }, []);

  useEffect(() => {
    if (updateAvailable && ['staging', 'production'].includes(__STAGE__)) {
      toast.warning(t('update-available'), {
        id: 'update-available',
        duration: Infinity,
        action: <UpdateToastAction />,
      });
    }
  }, [updateAvailable, t]);

  return (
    <Router>
      <MediaDropLabels>
        <InviteModalProvider>
          <OnboardingRedirect />
          <FeatureWizardHost />
          <InitiateCheckoutTracker />
          <NotificationEmailAttribution />
          <UserDataLayer />
          <Suspense fallback={null}>
            <AnimatedRoutes />
          </Suspense>
        </InviteModalProvider>
      </MediaDropLabels>
    </Router>
  );
};

export default App;
